import React, { useEffect, useState } from 'react'
import { Draggable } from 'react-beautiful-dnd'
import classNames from 'classnames'
import ReactDOM from 'react-dom'

import { DragAndDropButton } from './DragAndDropButton'

// To enable nested drag and drop, the `outer` property has been added, which will adjust the margins
/**
 * @import import { Drag } from '@/components/dragDrop'
 * @param {object} props
 * @param {string} props.draggableId Unique id for react-beautiful-dnd
 * @param {number} props.index Position of the row within its Drop list
 * @param {boolean} [props.alwaysShowIcon] Keeps the grip visible instead of hover-only
 * @param {boolean} [props.outer] Styles the row as the outer level of a nested drag and drop
 * @param {number} [props.nestedEntriesCount] Badge count shown while dragging a row with nested items
 * @param {boolean} [props.readOnly] Hides the grip and disables dragging
 * @param {boolean} [props.isModal] Portals the row while dragging so it escapes the modal
 * @param {boolean} [props.isHidden] Hides the grip button
 * @param {boolean} [props.showDragStyle] Elevated styling while the row is dragged
 * @param {boolean} [props.reverse] Places the grip after the content
 * @param {string} [props.dataTestId] Test id for the inner row
 * @param {object} [props.style] Inline style passed to the inner row
 */
export const Drag = ({
  draggableId,
  index,
  alwaysShowIcon,
  outer,
  nestedEntriesCount,
  readOnly,
  isModal,
  isHidden,
  showDragStyle,
  reverse,
  dataTestId,
  style,
  ...props
}) => {
  const [portal, setPortal] = useState(null)

  useEffect(() => {
    const portalDiv = document.createElement('div')
    document.body.appendChild(portalDiv)
    setPortal(portalDiv)

    return () => {
      document.body.removeChild(portalDiv)
    }
  }, [])

  return (
    // readOnly hides the grip (see DragAndDropButton) and disables the drag
    // itself — without isDragDisabled the row is still draggable by its hidden
    // handle, which no caller wants: every readOnly consumer passes it precisely
    // to stop reordering (a filtered column list, a locked playbook category, a
    // historical Notetaker version).
    <Draggable draggableId={draggableId} index={index} isDragDisabled={readOnly}>
      {(provided, snapshot) => {
        const row = (
          <div ref={provided.innerRef} {...provided.draggableProps} data-testid="draggable-entry">
            <div
              data-testid={dataTestId}
              style={style}
              className={classNames({
                'outer-draggable-item': outer,
                'draggable-item': !outer,
                'always-show-icon': alwaysShowIcon,
                'hidden-button-wrapper': !alwaysShowIcon && !outer,
                'hidden-button-wrapper-outer': !alwaysShowIcon && outer,
                'drag-style': showDragStyle && snapshot.isDragging,
                reverse,
              })}
            >
              <DragAndDropButton
                snapshot={snapshot}
                provided={provided}
                outer={outer}
                readOnly={readOnly}
                isHidden={isHidden}
                compact
              />
              {nestedEntriesCount > 0 && snapshot.isDragging && (
                <div className="nested-items-badge">{nestedEntriesCount}</div>
              )}
              {props.children}
            </div>
          </div>
        )

        if (isModal && snapshot.isDragging) {
          provided.draggableProps.style = {
            ...provided.draggableProps.style,
            left: provided.draggableProps.style.offsetLeft,
            top: provided.draggableProps.style.offsetTop,
          }

          return ReactDOM.createPortal(row, portal)
        }

        return row
      }}
    </Draggable>
  )
}
